import axios from 'axios';
import Config from '../utils/config';
import {
    createAuthRequestInterceptor,
    createResponseSuccessInterceptor,
    createResponseErrorInterceptor,
    handleRequestError
} from '../utils/http-interceptors.js';

// HTTP Configuration constants
const NON_RECOVERABLE_STATUSES = [401, 403, 500];

const DEFAULT_INTERCEPTOR_CONFIG = {
    nonRecoverableStatuses: NON_RECOVERABLE_STATUSES,
    showSuccessToasts: true,
    logoutUrl: '/account/logout',
    redirectDelay: 150
};

// API endpoint configuration
function ensureTrailingSlash(url) {
    return url.endsWith('/') ? url : `${url}/`;
}

const apiDomain = ensureTrailingSlash(Config.app.apiUrl);
const fronteggDomain = ensureTrailingSlash(Config.frontegg.baseUrl);

// Create axios instances
const instance = axios.create({
    baseURL: apiDomain
});

const fronteggInstance = axios.create({
    baseURL: fronteggDomain
});

// Use interceptor configuration
const interceptorConfig = DEFAULT_INTERCEPTOR_CONFIG;

// Setup interceptors for main API instance
instance.interceptors.request.use(createAuthRequestInterceptor(), handleRequestError);

instance.interceptors.response.use(createResponseSuccessInterceptor(interceptorConfig), createResponseErrorInterceptor(interceptorConfig));

// Setup interceptors for Frontegg instance
fronteggInstance.interceptors.request.use(createAuthRequestInterceptor(), handleRequestError);

fronteggInstance.interceptors.response.use(createResponseSuccessInterceptor(interceptorConfig), createResponseErrorInterceptor(interceptorConfig));

// HTTP method implementations - kept separate for extensibility
export default {
    /**
     * GET request to main API
     * @param {string} method - API endpoint
     * @param {Object} customHeaderObject - Additional headers
     * @param {boolean} hideToast - Whether to suppress toast notifications
     * @returns {Promise<*>} Response data
     */
    async get(method, customHeaderObject = {}, hideToast = false) {
        const res = await instance.get(method, {
            hideToast,
            headers: customHeaderObject
        });

        return res.data;
    },

    /**
     * POST request to main API
     * @param {string} method - API endpoint
     * @param {Object} body - Request payload
     * @param {Object} customHeaderObject - Additional headers
     * @param {boolean} hideToast - Whether to suppress toast notifications
     * @returns {Promise<*>} Response data
     */
    async post(method, body = {}, customHeaderObject = {}, hideToast = false) {
        const res = await instance.post(method, body, {
            hideToast,
            headers: customHeaderObject
        });

        return res.data;
    },

    /**
     * PUT request to main API
     * @param {string} method - API endpoint
     * @param {Object} body - Request payload
     * @param {Object} customHeaderObject - Additional headers
     * @param {boolean} hideToast - Whether to suppress toast notifications
     * @returns {Promise<*>} Response data
     */
    async put(method, body = {}, customHeaderObject = {}, hideToast = false) {
        const res = await instance.put(method, body, {
            hideToast,
            headers: customHeaderObject
        });

        return res.data;
    },

    /**
     * DELETE request to main API
     * @param {string} method - API endpoint
     * @param {Object} customHeaderObject - Additional headers
     * @param {boolean} hideToast - Whether to suppress toast notifications
     * @returns {Promise<*>} Response data
     */
    async delete(method, customHeaderObject = {}, hideToast = false) {
        const res = await instance.delete(method, {
            hideToast,
            headers: customHeaderObject
        });

        return res.data;
    },

    /**
     * GET request to Frontegg API
     * @param {string} method - API endpoint
     * @param {Object} customHeaderObject - Additional headers
     * @param {boolean} hideToast - Whether to suppress toast notifications
     * @returns {Promise<*>} Response data
     */
    async fronteggGet(method, customHeaderObject = {}, hideToast = false) {
        const res = await fronteggInstance.get(method, {
            hideToast,
            headers: customHeaderObject
        });

        return res.data;
    },

    // Expose instances for advanced usage
    instance,
    fronteggInstance
};
